import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  KpiCard,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useToast,
  type Tone,
} from '@cartly/ui';
import { PhoneCall } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

type Purpose = 'cod_confirmation' | 'order_enquiry' | 'delivery_confirmation' | 'support';

interface Overview {
  provider: { key: string; label: string; available: boolean; reason: string | null };
  settings: {
    enabled: boolean;
    activatedAt: string | null;
    currency: string;
    monthlyCap: number | null;
  };
  monthSpend: number;
  counts: Record<string, number>;
}
interface ConsentRow {
  id: string;
  name: string | null;
  phone: string | null;
  status: 'granted' | 'revoked';
  basis: string;
  recordedAt: string;
}
interface SessionRow {
  id: string;
  name: string | null;
  phone: string | null;
  purpose: Purpose;
  status: string;
  blockedReason: string | null;
  durationSeconds: number | null;
  outcome: string | null;
  error: string | null;
  createdAt: string;
}
interface SessionDetail {
  id: string;
  name: string | null;
  phone: string | null;
  status: string;
  outcome: string | null;
  summary: string | null;
  error: string | null;
  durationSeconds: number | null;
  cost: number;
  transcript: { role: 'agent' | 'customer'; text: string; at: string }[];
}
interface NumberRow {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string | null;
  callingStatus: string | null;
  error: string | null;
}
interface Gate {
  gate: string;
  ok: boolean;
  message: string;
}

/** The one call type that can be placed today; the others are listed in the log for later. */
const PURPOSES: { value: Purpose; label: string }[] = [
  { value: 'cod_confirmation', label: 'COD confirmation' },
];
const STATUS_TONE: Record<string, Tone> = {
  blocked: 'warning',
  queued: 'neutral',
  in_progress: 'neutral',
  completed: 'success',
  failed: 'critical',
  no_answer: 'warning',
};
const label = (s: string) => s.replace(/_/g, ' ');

/** AI calling: activation, spending cap, call consent and the call log. */
export function AiCallingPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const manage = can('calls:manage');
  const overview = useLoader(() => api.get<Overview>('/calls/overview'), tenantId);
  const consents = useLoader(
    () => api.get<{ consents: ConsentRow[] }>('/calls/consents').then((r) => r.consents),
    tenantId,
  );
  const sessions = useLoader(
    () => api.get<{ sessions: SessionRow[] }>('/calls/sessions').then((r) => r.sessions),
    tenantId,
  );
  const numbers = useLoader(
    () => api.get<{ numbers: NumberRow[] }>('/calls/numbers').then((r) => r.numbers),
    tenantId,
  );
  const [togglingNumber, setTogglingNumber] = useState<string | null>(null);
  const toast = useToast();
  const settingsForm = useSubmit();
  const consentForm = useSubmit();
  const callForm = useSubmit();
  const [cap, setCap] = useState<string | null>(null);
  const [consentPhone, setConsentPhone] = useState('');
  const [consentBasis, setConsentBasis] = useState('');
  const [callPhone, setCallPhone] = useState('');
  const [purpose, setPurpose] = useState<Purpose>('cod_confirmation');
  const [viewing, setViewing] = useState<string | null>(null);
  const [gates, setGates] = useState<Gate[] | null>(null);

  if (overview.loading && !overview.data) return <Spinner size="lg" label="Loading" />;
  if (overview.error || !overview.data) {
    return <Alert tone="critical">{overview.error ?? 'Failed to load'}</Alert>;
  }
  const o = overview.data;
  const capValue = cap ?? (o.settings.monthlyCap === null ? '' : String(o.settings.monthlyCap));

  const saveSettings = async (enabled: boolean) => {
    const monthlyCap = capValue.trim() === '' ? null : Number(capValue);
    const res = await settingsForm.run(() => api.put('/calls/settings', { enabled, monthlyCap }));
    if (res) {
      toast.success(enabled ? 'AI calling activated' : 'Settings saved');
      setCap(null);
      void overview.reload();
    }
  };
  const toggleCalling = async (n: NumberRow, enabled: boolean) => {
    setTogglingNumber(n.id);
    try {
      await api.post(`/calls/numbers/${n.id}/calling`, { enabled });
      toast.success(enabled ? 'WhatsApp Calling enabled' : 'WhatsApp Calling disabled');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setTogglingNumber(null);
      void numbers.reload();
    }
  };
  const saveConsent = async (e: FormEvent, status: 'granted' | 'revoked') => {
    e.preventDefault();
    const res = await consentForm.run(() =>
      api.put('/calls/consents', { phone: consentPhone, status, basis: consentBasis }),
    );
    if (res) {
      toast.success(status === 'granted' ? 'Consent recorded' : 'Consent revoked');
      setConsentPhone('');
      setConsentBasis('');
      void consents.reload();
    }
  };
  const requestCall = async (e: FormEvent) => {
    e.preventDefault();
    const res = await callForm.run(() =>
      api.post<{ placed: boolean; gates: Gate[] }>('/calls/sessions', {
        phone: callPhone,
        purpose,
      }),
    );
    if (res) {
      setGates(res.gates);
      void sessions.reload();
      void overview.reload();
    }
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="AI Calling"
        description="A voice agent that phones customers on WhatsApp to confirm cash-on-delivery orders. Calls need activation, a spending cap, the customer's recorded consent and call permission, and a customer with a COD order waiting for an answer."
      />

      {!o.provider.available && (
        <Alert tone="warning" title={`${o.provider.label}: voice calls unavailable`}>
          {o.provider.reason} Requests are recorded and refused; nothing is dialled and nothing is
          charged.
        </Alert>
      )}

      <div className="kpi-grid">
        <KpiCard
          label="Status"
          value={o.settings.enabled ? 'Activated' : 'Off'}
          hint={
            o.settings.activatedAt ? `since ${formatRelative(o.settings.activatedAt)}` : undefined
          }
        />
        <KpiCard
          label="Spent this month"
          value={`${o.settings.currency} ${o.monthSpend.toFixed(2)}`}
          hint={
            o.settings.monthlyCap === null
              ? 'No cap set'
              : `cap ${o.settings.currency} ${o.settings.monthlyCap}`
          }
        />
        <KpiCard
          label="Calls this month"
          value={Object.values(o.counts).reduce((a, b) => a + b, 0)}
        />
        <KpiCard label="Refused" value={o.counts['blocked'] ?? 0} />
      </div>

      {manage && (
        <Card
          title="Activation and spending cap"
          subtitle="AI calling stays off until you set a monthly cap and activate it."
        >
          <div className="stack">
            {settingsForm.error && <Alert tone="critical">{settingsForm.error}</Alert>}
            <TextField
              label={`Monthly spending cap (${o.settings.currency})`}
              value={capValue}
              onChange={(e) => setCap(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              hint="Whole rupees. Calls are refused once this month's cost reaches the cap."
            />
            <div className="row">
              <Button
                variant="primary"
                loading={settingsForm.pending}
                onClick={() => void saveSettings(true)}
              >
                {o.settings.enabled ? 'Save cap' : 'Activate AI calling'}
              </Button>
              {o.settings.enabled && (
                <Button
                  variant="secondary"
                  loading={settingsForm.pending}
                  onClick={() => void saveSettings(false)}
                >
                  Turn off
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card
        title="WhatsApp Calling on your numbers"
        subtitle="Calling must be enabled on a number in Meta before it can call customers. The customer must also allow calls from the business on WhatsApp."
        flush
      >
        {!numbers.data || numbers.data.length === 0 ? (
          <EmptyState icon={<PhoneCall size={22} />} title="No WhatsApp number connected">
            Connect a WhatsApp account first.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Calling</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {numbers.data.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <div className="table__primary mono">{n.displayPhoneNumber}</div>
                      {n.verifiedName && <div className="table__secondary">{n.verifiedName}</div>}
                    </td>
                    <td>
                      {n.callingStatus === null ? (
                        <Badge tone="warning">unknown</Badge>
                      ) : (
                        <Badge tone={n.callingStatus === 'ENABLED' ? 'success' : 'neutral'}>
                          {n.callingStatus.toLowerCase()}
                        </Badge>
                      )}
                      {n.error && <div className="table__secondary">{n.error}</div>}
                    </td>
                    <td className="table__actions">
                      {manage && n.callingStatus !== null && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={togglingNumber === n.id}
                          onClick={() => void toggleCalling(n, n.callingStatus !== 'ENABLED')}
                        >
                          {n.callingStatus === 'ENABLED' ? 'Disable calling' : 'Enable calling'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Call consent"
        subtitle="A customer can only be called with a consent on record, and the basis is kept."
      >
        {manage && (
          <form className="stack" onSubmit={(e) => void saveConsent(e, 'granted')}>
            {consentForm.error && <Alert tone="critical">{consentForm.error}</Alert>}
            <div className="form-grid">
              <TextField
                label="Customer phone"
                value={consentPhone}
                onChange={(e) => setConsentPhone(e.target.value)}
                inputMode="tel"
                hint="International format, e.g. 919876543210."
                required
              />
              <TextField
                label="Basis"
                value={consentBasis}
                onChange={(e) => setConsentBasis(e.target.value)}
                hint="Why we may call, e.g. “ticked at checkout on 2026-10-15”."
                required
              />
            </div>
            <div className="row">
              <Button type="submit" variant="primary" loading={consentForm.pending}>
                Record consent
              </Button>
              <Button
                variant="secondary"
                loading={consentForm.pending}
                onClick={(e) => void saveConsent(e, 'revoked')}
              >
                Revoke consent
              </Button>
            </div>
          </form>
        )}
        {!consents.data || consents.data.length === 0 ? (
          <EmptyState icon={<PhoneCall size={22} />} title="No call consent recorded">
            Record a customer&apos;s consent above before requesting a call.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Basis</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {consents.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="table__primary">{c.name ?? c.phone}</div>
                      {c.name && <div className="table__secondary mono">{c.phone}</div>}
                    </td>
                    <td>
                      <Badge tone={c.status === 'granted' ? 'success' : 'neutral'}>
                        {c.status}
                      </Badge>
                    </td>
                    <td>{c.basis}</td>
                    <td>{formatRelative(c.recordedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {manage && (
        <Card
          title="Request a call"
          subtitle="Checks every requirement and records the request. When all of them pass, the call is placed to the customer's WhatsApp straight away."
        >
          <form className="stack" onSubmit={(e) => void requestCall(e)}>
            {callForm.error && <Alert tone="critical">{callForm.error}</Alert>}
            <div className="form-grid">
              <TextField
                label="Customer phone"
                value={callPhone}
                onChange={(e) => setCallPhone(e.target.value)}
                inputMode="tel"
                required
              />
              <SelectField
                label="Purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as Purpose)}
                options={PURPOSES}
              />
            </div>
            <div>
              <Button type="submit" variant="primary" loading={callForm.pending}>
                Check and request
              </Button>
            </div>
            {gates && (
              <ul className="stack">
                {gates.map((g) => (
                  <li key={g.gate} className="row">
                    <Badge tone={g.ok ? 'success' : 'critical'}>{g.ok ? 'ok' : 'blocked'}</Badge>
                    <span>{g.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </form>
        </Card>
      )}

      <TranscriptModal id={viewing} onClose={() => setViewing(null)} />

      <Card title="Call log" subtitle="Most recent 50 requests." flush>
        {!sessions.data || sessions.data.length === 0 ? (
          <EmptyState icon={<PhoneCall size={22} />} title="No calls yet">
            Requested calls and their outcomes appear here.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Purpose</th>
                  <th>Status</th>
                  <th>Outcome</th>
                  <th>Requested</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="table__primary">{s.name ?? s.phone}</div>
                      {s.name && <div className="table__secondary mono">{s.phone}</div>}
                    </td>
                    <td>{label(s.purpose)}</td>
                    <td>
                      <Badge tone={STATUS_TONE[s.status] ?? 'neutral'}>{label(s.status)}</Badge>
                      {s.blockedReason && (
                        <div className="table__secondary">{label(s.blockedReason)}</div>
                      )}
                    </td>
                    <td>
                      {s.outcome ? label(s.outcome) : '—'}
                      {s.error && <div className="table__secondary">{s.error}</div>}
                    </td>
                    <td>{formatRelative(s.createdAt)}</td>
                    <td>
                      {['completed', 'no_answer', 'failed'].includes(s.status) && (
                        <Button size="sm" variant="ghost" onClick={() => setViewing(s.id)}>
                          Transcript
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** What was said on one call, and how it ended. */
function TranscriptModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const detail = useLoader(
    () => (id ? api.get<SessionDetail>(`/calls/sessions/${id}`) : Promise.resolve(null)),
    id,
  );
  const d = detail.data;
  return (
    <Modal open={id !== null} title="Call transcript" onClose={onClose}>
      {detail.loading && !d ? (
        <Spinner label="Loading" />
      ) : detail.error ? (
        <Alert tone="critical">{detail.error}</Alert>
      ) : d ? (
        <div className="stack">
          <div className="text-muted">
            {d.name ?? d.phone}
            {d.durationSeconds != null ? ` · ${d.durationSeconds}s` : ''}
            {d.cost > 0 ? ` · ₹${d.cost}` : ''}
          </div>
          {d.summary && <Alert tone="info">{d.summary}</Alert>}
          {d.error && <Alert tone="critical">{d.error}</Alert>}
          {d.transcript.length === 0 ? (
            <p className="text-muted">Nothing was said on this call.</p>
          ) : (
            <ul className="stack">
              {d.transcript.map((t, i) => (
                <li key={i}>
                  <strong>{t.role === 'agent' ? 'Agent' : 'Customer'}:</strong> {t.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
